import { useApolloClient, useMutation, gql } from '@apollo/client';
import React from 'react';
import EventsSynchronizer from '~Components/EventsSynchronizer';
import {
  AddReportEvents,
  AddReportEventsVariables,
  GetReportEvents,
  GetReportEventsVariables,
  GetDeviceReportEvents,
  GetDeviceReportEventsVariables,
  GetReportsSnapshot,
} from './__queries__';
import { useLogger, useMetadata } from '~Hooks';
import moment from 'moment';
import { useStorage } from '~Components/Storage';

const SNAPSHOTS_QUERY = gql`
  query GetReportsSnapshot($venueId: ID!) {
    venue: Venue(id: $venueId) {
      id
      eventReports {
        snapshot
      }
    }
  }
`;

const DEVICE_SPECIFIC_SNAPSHOT_QUERY = gql`
  query GetDeviceReportSnapshot($venueId: String!, $deviceName: String!, $date: DateTime!) {
    reportDeviceSnapshot(venueId: $venueId, deviceName: $deviceName, date: $date) {
      snapshot
      isPartial
    }
  }
`;

const EVENTS_QUERY = gql`
  query GetReportEvents($venueId: String!, $sentAfter: DateTime!) {
    reportEvents(venueId: $venueId, sentAfter: $sentAfter) {
      lastKey
      items
    }
  }
`;

const DEVICE_SPECIFIC_EVENTS_QUERY = gql`
  query GetDeviceReportEvents($venueId: String!, $sentAfter: DateTime!, $deviceName: String!) {
    reportDeviceEvents(venueId: $venueId, sentAfter: $sentAfter, deviceName: $deviceName) {
      lastKey
      items
    }
  }
`;

const ADD_EVENT_MUTATION = gql`
  mutation AddReportEvents($venueId: String!, $events: Json!) {
    addReportEvents(venueId: $venueId, events: $events) {
      success
      message
    }
  }
`;

interface Props {
  venueId: string;
}

/**
 * Retry settings for the report events mutation
 */
const MAX_RETRIES = 3;
const RETRY_DELAY_MS = 4000;

const ReportsSynchronizer: React.FunctionComponent<Props> = ({ venueId }) => {
  const client = useApolloClient();
  const metadata = useMetadata();
  const [storage] = useStorage();
  const logger = useLogger('reports-synchronizer');

  const queryDeviceEvents = async (timestamp: string) => {
    const response = await client.query<GetDeviceReportEvents, GetDeviceReportEventsVariables>({
      query: DEVICE_SPECIFIC_EVENTS_QUERY,
      variables: {
        venueId,
        sentAfter: timestamp,
        deviceName: storage.taxComplianceDeviceName || 'Unknown',
      },
      fetchPolicy: 'network-only',
    });

    return {
      success: Boolean(response.data?.reportDeviceEvents?.items),
      events: response.data?.reportDeviceEvents?.items || [],
      error: response.error?.message || null,
    };
  };

  const queryEvents = async (timestamp: string) => {
    const response = await client.query<GetReportEvents, GetReportEventsVariables>({
      query: EVENTS_QUERY,
      variables: {
        venueId,
        sentAfter: timestamp,
      },
      fetchPolicy: 'network-only',
    });

    return {
      success: Boolean(response.data?.reportEvents?.items),
      events: response.data?.reportEvents?.items || [],
      error: response.error?.message || null,
    };
  };

  const queryDeviceSnapshot = async () => {
    const DEFAULT_TIMESTAMP = moment()
      .startOf('day')
      .add(4, 'hours')
      .toISOString();

    const response = await client.query({
      query: DEVICE_SPECIFIC_SNAPSHOT_QUERY,
      variables: {
        venueId,
        deviceName: storage.taxComplianceDeviceName || 'Unknown',
        DEFAULT_TIMESTAMP,
      },
      fetchPolicy: 'network-only',
    });

    const snapshot = response.data?.reportDeviceSnapshot?.snapshot;

    return {
      success: Boolean(snapshot),
      snapshot: snapshot || null,
      timestamp: snapshot?.date || null,
      error: response.error?.message || null,
    };
  };

  const querySnapshot = async () => {
    const response = await client.query<GetReportsSnapshot>({
      query: SNAPSHOTS_QUERY,
      variables: {
        venueId,
      },
      fetchPolicy: 'network-only',
    });

    const snapshot = response.data?.venue?.eventReports?.snapshot;

    return {
      success: Boolean(snapshot),
      snapshot: snapshot || null,
      timestamp: snapshot?.date || null,
      error: response.error?.message || null,
    };
  };

  /**
   * Set up the add table events mutation
   */

  const [addReportEvents] = useMutation<AddReportEvents, AddReportEventsVariables>(
    ADD_EVENT_MUTATION,
  );

  return (
    <EventsSynchronizer
      venueId={venueId}
      eventType="ADD_REPORT_EVENT"
      snapshotType="SET_REPORT_SNAPSHOT"
      eventSelector={(state) => state.reportEvents}
      snapshotSelector={(state) => state.reportSnapshot}
      pollingInterval={30000}
      /* Polling query for new events */
      queryEvents={metadata.data.hasIndividualEOD ? queryDeviceEvents : queryEvents}
      /* Snapshot query */
      querySnapshot={metadata.data.hasIndividualEOD ? queryDeviceSnapshot : querySnapshot}
      addEventMutation={async (events) => {
        const runMutation = async () => {
          try {
            const response = await addReportEvents({
              variables: {
                venueId,
                events,
              },
            });

            if (!response.data?.addReportEvents?.success) {
              return {
                success: false,
                error: response.errors?.map((error) => error.message).join(', ') || 'Unknown',
              };
            }

            return {
              error: null,
              success: true,
            };
          } catch (e) {
            return {
              success: false,
              error: e instanceof Error ? e.message : 'Unknown error',
            };
          }
        };

        /**
         * Attempt the mutation, retrying up to MAX_RETRIES times with RETRY_DELAY_MS between attempts before giving up.
         */
        let result = await runMutation();

        for (let attempt = 1; attempt <= MAX_RETRIES && !result.success; attempt += 1) {
          logger.error('Failed to send report events, retrying', {
            attempt,
            maxRetries: MAX_RETRIES,
            errorMessage: result.error,
          });
          await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY_MS));
          result = await runMutation();
        }

        if (!result.success) {
          logger.error('Failed to send report events after retries', {
            attempts: MAX_RETRIES + 1,
            errorMessage: result.error,
          });
        }

        return result;
      }}
    />
  );
};

export default ReportsSynchronizer;
